import { InventoryType } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';

export type InventoryDashboardKPI = {
  key: string;
  label: string;
  value: number;
  meta?: Record<string, any>;
};

export type InventoryDashboardChartPoint = {
  key: string;
  label: string;
  value: number;
};

export type InventoryDashboardReceiptDay = {
  date: string;
  overdue: number;
  due_today: number;
  upcoming: number;
};

export type InventoryDashboardShortageRow = {
  part_id: number;
  part_number: string;
  description?: string | null;
  open_short_qty: number;
  open_log_count: number;
  last_shortage_at?: string | null;
};

export type InventoryDashboardRecentActivity = {
  type: 'RECEIPT' | 'QC' | 'CONSUMPTION' | 'FABRICATION_OUTPUT';
  title: string;
  subtitle?: string | null;
  quantity?: number;
  created_at: string;
};

export type InventoryDashboardInventoryRow = {
  row_key: string;
  source_type: 'PART' | 'MANUAL';
  part_id?: number | null;
  part_number: string;
  description?: string | null;
  available_qty: number;
  on_hold_qty: number;
  net_available_qty: number;
  incoming_open_qty: number;
  overdue_incoming_qty: number;
  pending_qc_qty: number;
  consumed_30d_qty: number;
  fabricated_30d_qty: number;
  open_shortage_qty: number;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'INCOMING_ONLY';
};

export type InventoryDashboardResponse = {
  project_id: number;
  updated_at: string;
  low_stock_threshold: number;
  window_days: number;
  kpis: InventoryDashboardKPI[];
  stock_status_breakdown: InventoryDashboardChartPoint[];
  receipts_by_day: InventoryDashboardReceiptDay[];
  top_shortage_parts: InventoryDashboardShortageRow[];
  recent_activity: InventoryDashboardRecentActivity[];
  inventory_table: InventoryDashboardInventoryRow[];
};

export type InventoryDashboardArgs = {
  params: {
    project_id: number | string;
  };
  query?: {
    low_stock_threshold?: number;
    window_days?: number;
    lookback_days?: number;
  };
};

export type args = {
  patchConfirmedReceipt: ApiType<InventoryType.ConfirmedReceipts.Patch>['args'];
  getConfirmedReceiptsAll: ApiType<InventoryType.ConfirmedReceipts.Get>['args'];

  getInventoryDashboard: InventoryDashboardArgs;
};

export type responses = {
  getConfirmedReceiptsAll: ApiType<InventoryType.ConfirmedReceipts.Get>['response'];

  getInventoryDashboard: InventoryDashboardResponse;
};

export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Inventory'],
  endpoints: (builder) => ({
    getConfirmedReceiptsAll: builder.query<
      responses['getConfirmedReceiptsAll'],
      args['getConfirmedReceiptsAll']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/inventory/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Inventory', id: 'confirmed-receipts' }],
    }),

    patchConfirmedReceipt: builder.mutation<
      void,
      args['patchConfirmedReceipt']
    >({
      query: (args) => ({
        url: `/production/inventory/${args.params.inventory_id}/hold/`,
        method: 'PATCH',
        body: args.body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: () => [
        { type: 'Inventory', id: 'confirmed-receipts' },
        { type: 'Inventory', id: 'dashboard' },
      ],
    }),

    getInventoryDashboard: builder.query<
      responses['getInventoryDashboard'],
      args['getInventoryDashboard']
    >({
      query: ({ params, query }) => ({
        url: `/production/projects/${params.project_id}/inventory/dashboard/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: [{ type: 'Inventory', id: 'dashboard' }],
    }),
  }),
});

export const {
  useGetConfirmedReceiptsAllQuery,
  usePatchConfirmedReceiptMutation,

  useGetInventoryDashboardQuery,
} = inventoryApi;

export namespace InventoryApi {
  export const Api = inventoryApi;
  export type Args = args;
  export type Responses = responses;
}
